import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { getApiToken } from "../authUtils";
import urlconfig from "../config/urlconfig";
import ClientRegistration from "./ClientRegistration";
import {
  CircularProgress,
  Alert,
  Paper,
  Typography,
  Button,
  Chip,
  Box,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  TablePagination,
  Grid,
  Divider,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import GroupsIcon from "@mui/icons-material/Groups";
import VerifiedIcon from "@mui/icons-material/Verified";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CostsheetDrillDialog, {getQuarter} from "./CostsheetDrillDailog";
import BulkClientImport from "../helpers/BulkClientImport";

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, color }) => (
  <Paper
    elevation={2}
    sx={{
      p: 3,
      borderRadius: 3,
      transition: "0.25s",
      "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
    }}
  >
    <Box display="flex" justifyContent="space-between">
      <Box>
        <Typography variant="h4" fontWeight="bold" color={color}>
          {value}
        </Typography>
        <Typography color="text.secondary">{label}</Typography>
      </Box>
      <Box color={color}>{icon}</Box>
    </Box>
  </Paper>
);

// ─── Item -> flat record transform (mirrors AnalyticsDashboard's processRaw, simplified) ─
const getCustomTotal = (rows, key) => rows?.reduce((sum, r) => sum + (parseFloat(r[key]) || 0), 0) || 0;

const transformItemsToRecords = (items) =>
  (items || []).map((entry) => {
    const subItems = entry.items || [];
    const subSp = subItems.reduce((s, i) => s + (Number(i.sp) || 0) * (Number(i.qty) || 0), 0);
    const subPp = subItems.reduce((s, i) => s + (Number(i.pp) || 0) * (Number(i.qty) || 0), 0);

    const customSp = getCustomTotal(entry.customRows, "customTotalSp");
    const customPp = getCustomTotal(entry.customRows, "customTotalPp");

    const totalSp =
      subSp +
      customSp +
      (Number(entry.transportationTotalSp) || 0) +
      (Number(entry.customizationTotalSp) || 0) +
      (Number(entry.marketingTotalSp) || 0);
    const totalPp =
      subPp +
      customPp +
      (Number(entry.transportationTotalPp) || 0) +
      (Number(entry.customizationTotalPp) || 0) +
      (Number(entry.marketingTotalPp) || 0);

    const margin = totalSp - totalPp;

    return {
      salesOrderNo: entry.salesOrderNo,
      clientName: entry.clientName,
      date: entry.date,
      quarter: entry.date ? getQuarter(entry.date) : "—",
      costsheetfor: entry.costsheetfor,
      totalSp,
      totalPp,
      margin,
      marginPercentage: totalSp > 0 ? (margin / totalSp) * 100 : 0,
    };
  });

const UniqueClients = () => {
  const { instance, accounts } = useMsal();
  const account = accounts?.[0];
  const clientRegRef = useRef(null);

  const [rawNames, setRawNames] = useState([]);
  const [clients, setClients] = useState([]); // full ClientRegistration list, for rawClientNames lookup
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters / pagination
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // "all" | "mapped" | "unmapped"
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Drill-down state
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillTitle, setDrillTitle] = useState("");
  const [drillSubtitle, setDrillSubtitle] = useState("");
  const [drillRecords, setDrillRecords] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState(null);

  const sendAuthenticatedRequest = useCallback(
    async (url) => {
      const token = await getApiToken(instance, account);
      const res = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const text = await res.text();
      if (!res.ok) throw new Error(text || "API failed");
      try {
        return text ? JSON.parse(text) : {};
      } catch {
        return text;
      }
    },
    [instance, account]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [names, clientList] = await Promise.all([
        sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/unique-client-names`),
        sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/clients`),
      ]);
      setRawNames(names || []);
      setClients(clientList || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [account, sendAuthenticatedRequest]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Build a lookup: raw name -> legalClientName (if mapped)
  const mappingLookup = useMemo(() => {
    const lookup = {};
    clients.forEach((c) => {
      (c.rawClientNames || []).forEach((rn) => {
        lookup[rn] = c.legalClientName;
      });
    });
    return lookup;
  }, [clients]);

  // Build a lookup: raw name -> owning client id (if mapped), so the "Mapped" chip
  // knows which client to open for editing/unmapping.
  const mappingIdLookup = useMemo(() => {
    const lookup = {};
    clients.forEach((c) => {
      (c.rawClientNames || []).forEach((rn) => {
        lookup[rn] = c.id;
      });
    });
    return lookup;
  }, [clients]);

  // Stats over the FULL unfiltered list, so counts stay meaningful regardless of search/filter/page
  const stats = useMemo(() => {
    const total = rawNames.length;
    const mapped = rawNames.filter((n) => !!mappingLookup[n]).length;
    return { total, mapped, unmapped: total - mapped };
  }, [rawNames, mappingLookup]);

  // Apply search + mapped/unmapped filter
  const filteredNames = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rawNames.filter((name) => {
      const mappedTo = mappingLookup[name];

      if (filter === "mapped" && !mappedTo) return false;
      if (filter === "unmapped" && mappedTo) return false;

      if (q) {
        const haystack = `${name} ${mappedTo || ""}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }

      return true;
    });
  }, [rawNames, mappingLookup, search, filter]);

  // Reset to page 0 whenever the filtered set changes shape (new search/filter)
  useEffect(() => {
    setPage(0);
  }, [search, filter]);

  const paginatedNames = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredNames.slice(start, start + rowsPerPage);
  }, [filteredNames, page, rowsPerPage]);

  // ─── Drill-down: fetch cost sheets for a raw client name and open the dialog ─
  const openDrillDown = useCallback(
    async (rawName, mappedTo) => {
      setDrillTitle(mappedTo || rawName);
      setDrillSubtitle(mappedTo ? `Raw name: "${rawName}"` : "Not yet mapped to a legal client");
      setDrillRecords([]);
      setDrillError(null);
      setDrillOpen(true);
      setDrillLoading(true);
      try {
        const items = await sendAuthenticatedRequest(
          `${urlconfig.backend_api_base_url}/api/v2/items/by-client-name?name=${encodeURIComponent(rawName)}`
        );
        setDrillRecords(transformItemsToRecords(items));
      } catch (err) {
        setDrillError(err.message || "Failed to load cost sheets");
      } finally {
        setDrillLoading(false);
      }
    },
    [sendAuthenticatedRequest]
  );

  return (
    <Paper sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: "linear-gradient(135deg,#1976d2 0%,#42a5f5 100%)",
          color: "white",
        }}
      >
        <Typography variant="h4" fontWeight={700} color="white" gutterBottom>
          Client Mapping Dashboard
        </Typography>
        <Typography sx={{ opacity: 0.9, mb:5 }} color="white">
          Map raw client names to registered legal clients · Click any row to view its cost sheets
        </Typography>
        <BulkClientImport/>
      </Paper>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={4}>
          <StatCard icon={<GroupsIcon fontSize="large" />} label="Total Clients" value={stats.total} color="#1976d2" />
        </Grid>
        <Grid item xs={4}>
          <StatCard icon={<VerifiedIcon fontSize="large" />} label="Mapped" value={stats.mapped} color="#2e7d32" />
        </Grid>
        <Grid item xs={4}>
          <StatCard icon={<WarningAmberIcon fontSize="large" />} label="Unmapped" value={stats.unmapped} color="#ed6c02" />
        </Grid>
      </Grid>

      {/* Search + filter controls */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
          <TextField
            size="small"
            placeholder="Search client name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Paper>

        <ToggleButtonGroup value={filter} exclusive size="small" onChange={(e, val) => val && setFilter(val)}>
          <ToggleButton value="all">All ({stats.total})</ToggleButton>
          <ToggleButton value="mapped">Mapped ({stats.mapped})</ToggleButton>
          <ToggleButton value="unmapped">Unmapped ({stats.unmapped})</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Divider sx={{ mb: 1 }} />

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <>
          {filteredNames.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No client names match your search/filter.
            </Typography>
          ) : (
            <Box>
              {paginatedNames.map((name) => {
                const mappedTo = mappingLookup[name];

                return (
                  <Paper
                    key={name}
                    elevation={1}
                    onClick={() => openDrillDown(name, mappedTo)}
                    sx={{
                      mb: 2,
                      borderRadius: 3,
                      cursor: "pointer",
                      transition: "0.25s",
                      "&:hover": {
                        boxShadow: 5,
                        transform: "translateY(-2px)",
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                        <ReceiptLongIcon sx={{ color: "#94a3b8" }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Typography fontWeight={600} noWrap>
                              {name}
                            </Typography>
                            <OpenInNewIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {mappedTo ? `Mapped to ${mappedTo}` : "Not mapped yet"}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} onClick={(e) => e.stopPropagation()}>
                        {mappedTo ? (
                          <Chip
                            color="success"
                            label="Mapped"
                            onClick={() => clientRegRef.current?.openEditMapping(name, mappingIdLookup[name])}
                            sx={{ cursor: "pointer" }}
                          />
                        ) : (
                          <Button variant="contained" onClick={() => clientRegRef.current?.openWithPrefill(name)}>
                            Map Client
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}

          <TablePagination
            component="div"
            count={filteredNames.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}

      {/* Hidden-but-mounted: reuses your full ClientRegistration modal/table logic */}
      <Box sx={{ mt: 4 }}>
        <ClientRegistration ref={clientRegRef} onClientMapped={fetchAll} />
      </Box>

      {/* Drill-down dialog — same component AnalyticsDashboard uses */}
      {drillLoading ? (
        <Box
          sx={{
            display: drillOpen ? "flex" : "none",
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.4)",
            zIndex: 1300,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper sx={{ p: 4, borderRadius: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CircularProgress size={32} />
            <Typography color="text.secondary">Loading cost sheets…</Typography>
          </Paper>
        </Box>
      ) : (
        <CostsheetDrillDialog
          open={drillOpen}
          onClose={() => setDrillOpen(false)}
          title={drillTitle}
          subtitle={
            drillError
              ? undefined
              : `${drillSubtitle} · ${drillRecords.length} cost sheet${drillRecords.length === 1 ? "" : "s"}`
          }
          records={drillRecords}
          showQoQ
        />
      )}
      {drillError && drillOpen && !drillLoading && (
        <Box sx={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 1400 }}>
          <Alert severity="error" onClose={() => setDrillError(null)}>
            {drillError}
          </Alert>
        </Box>
      )}
    </Paper>
  );
};

export default UniqueClients;